import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {useAppQuery} from '../../../libs/shared/core/src/services/query/useAppQuery';
import {Banner, BannerTypesEnum} from '../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../libs/shared/ui/src/components/banners/bannersContext';
import {useLinkNavigation} from '../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {AiTaggingSampleApprovedModel} from '../../models/api/aiTaggingSampleApprovedModel';
import {TagModel} from '../../models/api/tagModel';
import {SettingsRuleResourcePagesEnum} from '../../models/url/models/rulesUrlModel';
import {SettingsRootPagesEnum} from '../../models/url/models/types';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {tagQueries} from '../../queries/tagQueries';
import {RuleComplexRecipeTypesEnum} from '../../settings/rules/ruleRecipeModel';

/*
 * Props.
 */

interface AiTaggingSampleApprovedBannerProps {
  onDismissClick: () => void;
  aiTaggingSampleApprovedModel: AiTaggingSampleApprovedModel;
}

/*
 * Styles.
 */

const StyledBanner = styled(Banner)`
  width: max-content;
`;

/*
 * Component.
 */

export const AiTaggingSampleApprovedBanner: FC<AiTaggingSampleApprovedBannerProps> = (props) => {
  const {aiTaggingSampleApprovedModel, onDismissClick} = props;
  const {count, totalNeeded, tagId} = aiTaggingSampleApprovedModel;

  const {navigateToLink} = useLinkNavigation();
  const theme = useUserTheme();

  const {data: tag} = useAppQuery(tagQueries.id(tagId));

  // Shouldn't happen
  if (!tag) {
    return null;
  }

  const isFirstApprove = count === 1;
  const isFinalApprove = count === totalNeeded;
  const remainingApproves = totalNeeded - count;

  const {title, description} = computeTitleAndDescription(
    isFirstApprove,
    isFinalApprove,
    remainingApproves,
    tag,
  );

  const ruleBundlePath = buildSettingsPath({
    rootPage: SettingsRootPagesEnum.RULES,
    namespace: aiTaggingSampleApprovedModel.namespace,
    resourcePage: SettingsRuleResourcePagesEnum.EDIT,
    recipe: RuleComplexRecipeTypesEnum.AI_TAGGING,
    bundleId: aiTaggingSampleApprovedModel.ruleBundleId,
    filters: undefined,
    inboxId: undefined,
    isInboxCreation: false,
    ruleId: undefined,
  });

  return (
    <StyledBanner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.green.shade40}
      priority={BannerPrioritiesEnum.INFORMATION}
    >
      <BannerCloseAction onClick={onDismissClick} />
      <BannerIcon iconName="magicWandFilledSmall" />
      <BannerContent>
        <BannerContentTitle>{title}</BannerContentTitle>
        {description}
      </BannerContent>
      {isFinalApprove && (
        <BannerFooterActions>
          <BannerAction
            onClick={() => {
              navigateToLink(ruleBundlePath);
              onDismissClick();
            }}
          >
            <Trans id="ai-tagging-review-go-to-rule-action">Go to rule</Trans>
          </BannerAction>
        </BannerFooterActions>
      )}
    </StyledBanner>
  );
};

/*
 * Helpers.
 */

function computeTitleAndDescription(
  isFirstApprove: boolean,
  isFinalApprove: boolean,
  remainingApproves: number,
  tag: TagModel,
) {
  if (isFirstApprove) {
    return {
      title: (
        <Trans id="ai-tagging-review-title-first-sample">Great job on your first approved sample!</Trans>
      ),
      description: (
        <Trans id="ai-tagging-review-description-first-sample">
          Approve <b>{remainingApproves}</b> more sample messages.
        </Trans>
      ),
    };
  }

  if (isFinalApprove) {
    return {
      title: (
        <Trans id="ai-tagging-review-title-done-collecting">
          Finished training tag: <b>{tag.name}</b> 🎉
        </Trans>
      ),
      description: (
        <Trans id="ai-tagging-review-description-done-collecting">
          AI tagging for <b>{tag.name}</b> is now enabled.{<br />}You can update this in your rule setting at
          any time.
        </Trans>
      ),
    };
  }

  return {
    title: <Trans id="ai-tagging-review-title-keep-going">Keep it going!</Trans>,
    description: (
      <Trans id="ai-tagging-review-description-keep-going">
        Approve <b>{remainingApproves}</b> more sample messages.
      </Trans>
    ),
  };
}
